import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getLocale, useIntl } from 'umi';
import { TableColumn } from 'tea-component';
import { formatExeclCommon, formatExeclNum, formatNum, getChartDateFormatString, handleDownload } from '@/pages/dashboard/utils/utils';
import CardBox from '@/pages/dashboard/components/CardBox';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import { xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import { useOption, useWeekDay, useWeekDayArr } from '@/pages/dashboard/utils/customHook';
import { barFormatterFunc } from '@/pages/dashboard/components/Chart/_config/utils';
import { usePcRevenueSelectorMap } from '@/pages/dashboard/filterPageComponents/monetization/hooks';
import { getBaseTooltip } from '@/pages/dashboard/components/EchartsTooltip/utils';
import { getName } from '@home/utils/common';

function SalesBreakdownCardCo(props: any) {
  const { data, config, formValue, bordered, byType, downloadTitle } = props;
  const { isLoading, isError, componentData } = data;
  const { height = 340, defaultAvtive = 1, tableDataKey, chartDataKey, chart: chartConfig,
    table: tableConfig, title: configTitle, hideConfig = [], dataFormat = 'number', tooltipConfig } = config;
  const { formatMessage } = useIntl();
  const { getWeekDay, getWeekDayStr } = useWeekDay();
  const { timeType = 'Day', selector } = formValue || {};
  const { getPcRevenueSelectorText } = usePcRevenueSelectorMap(selector);
  const [iseye, setIseye] = useState(true);
  const isEN = getLocale() === 'en-US';
  const weekDayArr = useWeekDayArr();

  const barChartRef = useRef<any>();
  const lineChartRef = useRef<any>();

  // 防止切换国际化时，浮窗不会同步国际化
  useEffect(() => {
    barChartRef.current?.refreshChart();
    lineChartRef.current?.refreshChart();
  }, [isEN]);

  const title = useMemo(() => {
    const selected = configTitle?.filter((val: { type: string; }) => val.type === byType)[0];
    return getName(isEN, selected?.name, selected?.nameCn);
  }, [configTitle, byType, isEN]);

  const { apiTableData, apiChartData } = useMemo(() => {
    const apiTableData = componentData?.[tableDataKey || 'dwm_data_list'] || [];
    const apiChartData = componentData?.[chartDataKey || 'dwm_list'] || [];
    return {
      apiTableData,
      apiChartData
    };
  }, [chartDataKey, componentData, tableDataKey]);

  // 浮窗配置
  const getTooltipText = (params: any) => {
    const {
      valueNameCn,
      valueNameEn,
      shareNameCn,
      shareNameEn,
      sumNameCn,
      sumNameEn,
    } = tooltipConfig;
    return getBaseTooltip({
      params,
      options: {
        isEn: isEN,
        valueNameCn,
        valueNameEn,
        shareNameCn,
        shareNameEn,
        sumNameCn,
        sumNameEn,
        formatFirstThText: getWeekDayStr
      }
    });
  };

  const formatConfig = useMemo(() => ({
    bar: {
      formatterFunc: (params: any[]) => {
        // 走新浮窗配置
        if (tooltipConfig) {
          return getTooltipText(params);
        }

        const newParams = params.map(item => {
          return {
            ...item,
            title: getWeekDay(item.title),
            axisValue: getChartDateFormatString(timeType, item.axisValue, true)  // 月颗粒度处理浮窗日期
          };
        });
        return barFormatterFunc(newParams, { isUserQuota: dataFormat !== 'money', isSort: true, isShowWeekDay: true, weekDayArr });
      },
      setOtherConfigFunc: (opt: any) => {
        // eslint-disable-next-line no-param-reassign
        opt.xAxis[0].axisLabel.formatter = (value: string | number) => getChartDateFormatString(timeType, value);
        return opt;
      },
    },
    line: {
      formatterFunc: (params: any[]) => {
        // 走新浮窗配置
        if (tooltipConfig) {
          return getTooltipText(params);
        }

        const newParams = params.map(item => {
          return {
            ...item,
            isShowWeekDay: true,
            axisValue: getChartDateFormatString(timeType, item.axisValue, true) // 月颗粒度处理浮窗日期
          };
        });
        return barFormatterFunc(newParams, { isUserQuota: dataFormat !== 'money', isSort: true, isShowWeekDay: true, weekDayArr });
      },
      setOtherConfigFunc: (opt: any) => {
        // eslint-disable-next-line no-param-reassign
        opt.xAxis[0].axisLabel.formatter = (value: string | number) => getChartDateFormatString(timeType, value);
        return opt;
      },
    }
  }), [timeType, dataFormat, isEN, weekDayArr]);



  const options = useMemo(() => {
    // 图表数组
    const chart: any[] = [];
    // 1,超出10条， others有值显示other
    // 2, 小于十条， 不显示others
    const metrics = apiChartData?.[0]?.list?.map((item: { [x: string]: any; }) => item?.[chartConfig?.labelKey || 'full_crz_name']);
    const isMoreThanTen = apiTableData?.length > 10;
    const othersIsNull = apiTableData?.find((item: { name: string; }) => item?.name === 'Others')?.value === '';
    const metricsList = (!othersIsNull && isMoreThanTen) ? metrics : metrics?.filter((item: any) => item?.toLowerCase() !== 'others') || [];
    metricsList?.map((metric: any) => {
      const columnObj = {
        name: getPcRevenueSelectorText(byType, metric),
        barCategoryGap: '50%',
        stack: "Total",
        data: [] as any[],
      };
      apiChartData?.map((item: any) => {
        columnObj.data.push({
          x: item?.[chartConfig?.dateKey || 'date_ymd'],
          y: item?.list?.find((v: { [x: string]: any; }) => v?.[chartConfig?.labelKey || 'full_crz_name'] === metric)?.value,
        });
      });
      columnObj.data.length && chart.push(columnObj);
    });
    // 表头固定列
    const tableOption: TableColumn[] = [
      {
        key: 'date',
        align: 'left',
        fixed: 'left',
        width: 160,
        header: formatMessage({ id: 'monetization_revenue_Date' }),
        render: (cvm: any) => {
          const weekDay = getWeekDay(cvm?.date);
          const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
          return <>
            <span className='table-text'>{getChartDateFormatString(timeType, cvm?.date, true)}</span>
            {timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
          </>;
        },
      },
    ];
    metricsList?.map((metric: any) => {
      const metricOption: any = {
        key: metric,
        width: tableConfig?.metricWidth || 140,
        align: 'right',
        header: getPcRevenueSelectorText(byType, metric),
      };
      tableOption.push(metricOption);
    });
    const tableData: any = [];
    const downloadData: any = [];

    apiChartData?.map((item: any) => {
      const date = getChartDateFormatString(timeType, item?.[tableConfig?.dateKey || 'date_ymd'], true);
      const obj: any = { date };
      const downObj: any = { date };
      item?.list?.map((v: { [x: string]: string | number; value: string | number | null; }) => {
        obj[v?.[tableConfig?.labelKey || 'full_crz_name']] = formatNum(v?.value || '-', false, dataFormat !== 'money');
        downObj[v?.[tableConfig?.labelKey || 'full_crz_name']] = dataFormat !== 'money' ? formatExeclNum(v?.value) : formatExeclCommon(v?.value ?? '-');
      });
      tableData.push(obj);
      downloadData.push(downObj);
    });

    return {
      chart,
      table: {
        option: tableOption,
        data: tableData,
        downloadData,
        downloadTitle
      },
    };
  }, [apiChartData, apiTableData, formatMessage, chartConfig?.labelKey, chartConfig?.dateKey, 
    getPcRevenueSelectorText, byType, getWeekDay, timeType, tableConfig?.dateKey, tableConfig?.labelKey, dataFormat, downloadTitle]);
  const { xAxisData, legendData, lineSeries, barSeries, isNodata } = useOption(options);

  const renderList = useMemo(() => [
    {
      type: 'eyes',
      svgType: iseye ? 'open-eyes' : 'eyes-closed',
      content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
      isHideCom: true,
    },
    {
      type: 'barChart',
      svgType: 'column-chart',
      content: formatMessage({ id: 'common_bar_chart' }),
      component: <BarChart
        ref={barChartRef}
        height={height}
        isStackBar={iseye}
        fixNumber={dataFormat === 'money' ? 2 : 0}
        legendData={xAxisData.length > 0 ? legendData : []}
        xAxisData={xAxisData || []}
        series={xAxisData.length > 0 ? barSeries : []}
        formatterFunc={formatConfig?.bar?.formatterFunc}
        setOtherConfigFunc={(opt: any) => {
          if (formatConfig?.bar?.setOtherConfigFunc) opt = formatConfig?.bar?.setOtherConfigFunc(opt);
          opt = xAxisHighLight(opt, {
            formatter: (params: { value: string; }) => getChartDateFormatString(timeType, params.value)
          });
          return opt;
        }}
      />
    },
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: 'common_line_chart' }),
      component: <LineChart
        ref={lineChartRef}
        height={height}
        legendData={xAxisData.length > 0 ? legendData : []}
        xAxisData={xAxisData || []}
        series={xAxisData.length > 0 ? lineSeries : []}
        formatterFunc={formatConfig?.line?.formatterFunc}
        setOtherConfigFunc={(opt: any) => {
          if (formatConfig?.line?.setOtherConfigFunc) opt = formatConfig?.line?.setOtherConfigFunc(opt);
          opt = xAxisHighLight(opt, {
            formatter: (params: { value: string; }) => getChartDateFormatString(timeType, params.value)
          });
          return opt;
        }}
      />
    },
    {
      type: 'sheet',
      svgType: 'sheet',
      content: formatMessage({ id: 'common_table' }),
      component: <MiniTable
        height={height}
        loading={false}
        tableOption={options?.table?.option}
        tableList={options?.table?.data}
      />
    }
  ], [barSeries, dataFormat, formatConfig?.bar, formatConfig?.line, formatMessage, height, iseye, legendData, lineSeries, options?.table?.data, options?.table?.option, timeType, xAxisData]);

  const filteredRenderList = useMemo(() => {
    if (hideConfig) {
      return renderList.filter((v) => !hideConfig.includes(v.type));
    }
    return renderList;
  }, [hideConfig, renderList]);

  return <CardBox
    className='register-table'
    height={height}
    bordered={bordered}
    titleCom={title}
    renderList={filteredRenderList || []}
    defaultAvtive={defaultAvtive}
    setCurrentDataType={(type: any) => {
      if (type === 'eyes') {
        setIseye(!iseye);
      }
    }}
    downloadClick={async () => {
      handleDownload(options?.table, title);
    }}
    isLoading={isLoading}
    isError={isError}
    isNodata={isNodata}
  />;
}

export default SalesBreakdownCardCo;
